import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import LoadingOverlay from '../components/Common/LoadingOverlay';
import { SettingTypeEnum } from '../enums/SettingTypeEnum';
import { StorageItemKeyEnum } from '../enums/StorageItemKeyEnum';
import { useAuth } from '../hooks/useAuth';
import { useTags } from '../hooks/useTags';
import BrandingData from '../interfaces/BrandingData';
import File from '../interfaces/File';
import Tag from '../interfaces/Tag';
import { FileService } from '../services/fileService';
import { SettingService } from '../services/settingService';
import { getStorageItem, storeStorageItem } from '../utils/storage';
import { getCompanyHash } from '../utils/utils';

interface PermissionsContextType {
  receivedFilesCount: number;
  isLoadingBrandingData: boolean;
  brandingData: BrandingData | null;
  getBrandingData: () => Promise<void>;
  getCountFilesUnread: () => Promise<void>;
  fileSizeLimitUpload: number;
  isDownloadAllowedForFiles: (filesToCheck: File[]) => boolean;
  getSettings: () => Promise<void>;
  tags: Tag[];
  forcedAntiCaptureMode: ForcedAntiCaptureMode;
  isCurrentFolderReadOnly: boolean;
  setIsCurrentFolderReadOnly: (isCurrentFolderReadOnly: boolean) => void;
}

interface DownloadRestriction {
  tags: string[];
  isActiveForCurrentUser: boolean;
  externalUserEnforcement: boolean;
}

interface ForcedAntiCaptureMode {
  tags: string[];
  isActiveForCurrentUser: boolean;
  externalUserEnforcement: boolean;
}

export const PermissionsContext = createContext<PermissionsContextType>({
  brandingData: null,
  getCountFilesUnread: async () => {},
  getBrandingData: async () => {},
  isLoadingBrandingData: false,
  receivedFilesCount: 0,
  fileSizeLimitUpload: 250,
  isDownloadAllowedForFiles: () => true,
  getSettings: async () => {},
  tags: [],
  forcedAntiCaptureMode: {
    tags: [],
    isActiveForCurrentUser: false,
    externalUserEnforcement: false,
  },
  isCurrentFolderReadOnly: false,
  setIsCurrentFolderReadOnly: () => {},
});

const fileService = new FileService();
const settingsService = new SettingService();

interface PermissionsProviderProps {
  children: ReactNode;
}

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({
  children,
}) => {
  const { isLoggedIn, authenticatedUser, isExternal, isAdminOrContributor } =
    useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tags } = useTags();
  const [receivedFilesCount, setReceivedFilesCount] = useState(0);
  const [isLoadingBrandingData, setIsLoadingBrandingData] = useState(false);
  const [brandingData, setBrandingData] = useState<BrandingData | null>(null);
  const [fileSizeLimitUpload, setFileSizeLimitUpload] = useState<number>(
    authenticatedUser?.license?.maxFileSizeMbForEncryption || 250
  );
  const [isCurrentFolderReadOnly, setIsCurrentFolderReadOnly] =
    useState<boolean>(false);
  const [areSettingsLoaded, setAreSettingsLoaded] = useState<boolean>(false);

  const [downloadRestriction, setDownloadRestriction] =
    useState<DownloadRestriction>({
      tags: [],
      isActiveForCurrentUser: false,
      externalUserEnforcement: false,
    });
  const [forcedAntiCaptureMode, setForcedAntiCaptureMode] =
    useState<ForcedAntiCaptureMode>({
      tags: [],
      isActiveForCurrentUser: false,
      externalUserEnforcement: false,
    });

  const isLoadingAvailableForRoute =
    location.pathname.startsWith('/file') ||
    location.pathname.startsWith('/users') ||
    location.pathname.startsWith('/groups') ||
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/sales') ||
    location.pathname.startsWith('/quick-share');

  /**
   * @description Get the count of files unread.
   */
  const getCountFilesUnread = useCallback(async () => {
    if (!isLoggedIn) {
      return;
    }

    await fileService
      .getCountFilesUnread()
      .then((response) => {
        setReceivedFilesCount(response);
      })
      .catch(() => {
        console.error('Error getting count files unread');
      });
  }, [isLoggedIn]);

  /**
   * Asynchronously retrieves multiple settings when the user is logged in.
   *
   * Utilizes the `settingsService` to fetch the settings specified
   * in `SettingTypeEnum.FileSizeLimit`. If there is an error during the fetch,
   * an error message is logged to the console.
   *
   * The function is memoized using `useCallback` to avoid unnecessary
   * re-creation on each render, and it only re-declares when `isLoggedIn`
   * dependency changes.
   *
   * @returns {Promise<void>} A promise that resolves when the settings are fetched and processed.
   */
  const getSettings = useCallback(async () => {
    if (!isLoggedIn) {
      setAreSettingsLoaded(true);
      return;
    }

    await settingsService
      .getMultipleSettings([
        SettingTypeEnum.FileSizeLimit,
        SettingTypeEnum.DownloadRestrictions,
        SettingTypeEnum.ForcedAntiCaptureMode,
      ])
      .then((response) => {
        // Check if response is not an array and convert it to an array
        if (!Array.isArray(response)) {
          response = [response];
        }

        response.forEach((setting) => {
          switch (setting.name) {
            case SettingTypeEnum.FileSizeLimit:
              {
                const parsedAdditionalData = JSON.parse(setting.additionalData);

                // Get the file size limit for uploads
                if (
                  parsedAdditionalData.fileSizeLimit &&
                  parseInt(parsedAdditionalData.fileSizeLimit) > 0
                ) {
                  setFileSizeLimitUpload(
                    parseInt(parsedAdditionalData.fileSizeLimit)
                  );
                } else {
                  setFileSizeLimitUpload(
                    authenticatedUser?.license?.maxFileSizeMbForEncryption ||
                      250
                  );
                }
              }
              break;
            case SettingTypeEnum.DownloadRestrictions:
              {
                const parsedAdditionalData = JSON.parse(setting.additionalData);

                let splitTags: string[] = [];
                if (parsedAdditionalData.tags) {
                  // Split the tags by comma and convert them to lowercase and remove whitespace

                  splitTags = parsedAdditionalData.tags
                    .split(',')
                    .map((tag: string) => tag.toLowerCase().trim());
                }

                setDownloadRestriction({
                  tags: splitTags,
                  isActiveForCurrentUser: setting.isActiveForCurrentUser,
                  externalUserEnforcement:
                    parsedAdditionalData.externalUserEnforcement,
                });
              }
              break;
            case SettingTypeEnum.ForcedAntiCaptureMode:
              {
                const parsedAdditionalData = JSON.parse(setting.additionalData);

                let splitTags: string[] = [];
                if (parsedAdditionalData.tags) {
                  // Split the tags by comma and convert them to lowercase and remove whitespace

                  splitTags = parsedAdditionalData.tags
                    .split(',')
                    .map((tag: string) => tag.toLowerCase().trim());
                }
                setForcedAntiCaptureMode({
                  tags: splitTags,
                  isActiveForCurrentUser: setting.isActiveForCurrentUser,
                  externalUserEnforcement:
                    parsedAdditionalData.externalUserEnforcement,
                });
              }
              break;
            default:
              break;
          }
        });

        setAreSettingsLoaded(true);
      })
      .catch((e) => {
        console.error('Error getting settings');
      });
  }, [isLoggedIn]);

  /**
   * Asynchronous function to fetch branding data.
   * This function constructs a URL using environment variables to locate a branding JSON file in an Azure storage container.
   * The function first retrieves a company hash, which is then used in the URL construction.
   * It fetches the branding data from the constructed URL and logs the data if successfully obtained.
   * If an error occurs during fetching, it logs the error to the console.
   *
   * @function
   * @name getBrandingData
   * @returns {void}
   */
  const getBrandingData = useCallback(async () => {
    setIsLoadingBrandingData(true);

    // Check if branding data is already stored in session storage
    const existingBrandingData = await getStorageItem(
      'sessionStorage',
      StorageItemKeyEnum.brandingData
    );

    // Set already present branding data for faster loading
    if (existingBrandingData) {
      setBrandingData(existingBrandingData);
    }

    const hash = await getCompanyHash();

    // Build url
    const url = `${import.meta.env.VITE_APP_AZURE_STORAGE_URL}/${import.meta.env.VITE_APP_AZURE_RESOURCES_CONTAINER}/${hash.toUpperCase()}/branding.json`;

    // Fetch branding data
    fetch(url, { cache: 'no-store' })
      .then((response) => response.json())
      .then((data) => {
        storeStorageItem(
          'sessionStorage',
          StorageItemKeyEnum.brandingData,
          data
        );
        setBrandingData(data);
      })
      .catch(() => {
        setBrandingData(null);
      })
      .finally(() => {
        setIsLoadingBrandingData(false);
      });
  }, []);

  /**
   * Determines whether downloading is allowed for a given file based on current download restrictions.
   * This function checks if there are active restrictions on downloading files and validates the tags
   * associated with the file against restricted tags.
   *
   *                      which is an array of tag identifiers.
   * @returns {boolean} - Returns true if the download is allowed; returns false if the file has tags that
   *                      are restricted under current download conditions.
   * @param fileToCheck
   */
  const isDownloadAllowedForFiles = useCallback(
    (filesToCheck: File[]): boolean => {
      if (!authenticatedUser?.userId) {
        return false;
      }

      if (!downloadRestriction) {
        // If there are no download restrictions, allow download
        return true;
      }

      // Loop through each file in the array
      for (const fileToCheck of filesToCheck) {
        if (!fileToCheck) {
          continue; // Skip if the file is null or undefined
        }

        // Find tag names for the file by searching in the tags array
        const fileTags = fileToCheck?.tags?.map((tagId) => {
          const tag = tags.find((t) => t.id === tagId);
          return tag ? tag.title.toLowerCase() : '';
        });

        // Check if any of the file tags are in the download restriction tags
        const isTagPresent = fileTags?.some((tag) =>
          downloadRestriction?.tags?.includes(tag.toLowerCase())
        );

        if (isExternal && downloadRestriction.externalUserEnforcement) {
          return false;
        }

        if (isTagPresent && downloadRestriction.isActiveForCurrentUser) {
          return false;
        }
      }

      // If all files passed the check, return true
      return true;
    },
    [tags, authenticatedUser, downloadRestriction, isExternal]
  );

  /**
   * @description Get the count of files unread on component mount.
   */
  useEffect(() => {
    // Check if location is /file/:id and avoid getting brandingData
    if (location.pathname.includes('/file/')) {
      return;
    }

    getCountFilesUnread().then(() => {});
  }, [getCountFilesUnread, isLoggedIn]);

  useEffect(() => {
    Promise.all([getSettings(), getBrandingData()]).then(() => {});
  }, [getSettings, getBrandingData]);

  useEffect(() => {
    const routesBlacklist = [
      '/files/personal/*',
      '/files/shared/*',
      '/files/trash',
      '/users/*',
      '/groups',
      '/settings/*',
    ];

    const path = location.pathname;
    const isBlacklisted = routesBlacklist.some((pattern) =>
      matchPath({ path: pattern, end: false }, path)
    );

    if (isBlacklisted && !isAdminOrContributor) {
      navigate('/');
    }
  }, [isAdminOrContributor, location.pathname]);

  const contextValue = useMemo(() => {
    return {
      getCountFilesUnread,
      brandingData,
      getBrandingData,
      isLoadingBrandingData,
      receivedFilesCount,
      fileSizeLimitUpload,
      isDownloadAllowedForFiles,
      getSettings,
      forcedAntiCaptureMode,
      tags,
      isCurrentFolderReadOnly,
      setIsCurrentFolderReadOnly,
    };
  }, [
    getCountFilesUnread,
    brandingData,
    getBrandingData,
    isLoadingBrandingData,
    receivedFilesCount,
    fileSizeLimitUpload,
    isDownloadAllowedForFiles,
    getSettings,
    forcedAntiCaptureMode,
    tags,
    isCurrentFolderReadOnly,
    setIsCurrentFolderReadOnly,
  ]);

  if (!isLoadingAvailableForRoute) {
    return (
      <PermissionsContext.Provider value={contextValue}>
        {children}
      </PermissionsContext.Provider>
    );
  }

  return (
    <PermissionsContext.Provider value={contextValue}>
      <LoadingOverlay
        label={t('common.loadingPermissions')}
        isVisible={!areSettingsLoaded}
      />

      {areSettingsLoaded && children}
    </PermissionsContext.Provider>
  );
};
